"use client";

import {
  createContext,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
} from "react";
import type { Key, ReactNode } from "react";
import { LoadingState } from "./LoadingState";

export type IndustrySetupWizardDirection = "forward" | "backward";
export type IndustrySetupWizardViewport = "fullscreen" | "embedded";
export type IndustrySetupWizardContentWidth = "narrow" | "wide" | "full";

export type IndustrySetupWizardLoadingState = {
  description?: ReactNode;
  detail?: ReactNode;
  eyebrow?: ReactNode;
  progressLabel: string;
  title: ReactNode;
};

export type IndustrySetupWizardShellProps = {
  auxiliaryAction?: ReactNode;
  backAction?: ReactNode;
  children: ReactNode;
  className?: string;
  contentWidth?: IndustrySetupWizardContentWidth;
  direction?: IndustrySetupWizardDirection;
  feedback?: ReactNode;
  focusOnStageChange?: boolean;
  headerEnd?: ReactNode;
  headerStart?: ReactNode;
  loading?: IndustrySetupWizardLoadingState | null;
  primaryAction?: ReactNode;
  progressAriaLabel?: string;
  progressLabel: string;
  showProgress?: boolean;
  stageKey: Key;
  stepCount: number;
  stepIndex: number;
  topbar?: ReactNode;
  viewport?: IndustrySetupWizardViewport;
};

export type IndustrySetupQuestionStageProps = {
  children?: ReactNode;
  className?: string;
  description?: ReactNode;
  eyebrow?: ReactNode;
  title: ReactNode;
};

type NormalizedProgress = {
  current: number;
  percent: number;
  total: number;
};

const INDUSTRY_SETUP_WIZARD_MOTION_STYLES = `
@keyframes industry-setup-wizard-stage-forward {
  from { opacity: 0; transform: translate3d(0, 10px, 0); }
  to { opacity: 1; transform: translate3d(0, 0, 0); }
}
@keyframes industry-setup-wizard-stage-backward {
  from { opacity: 0; transform: translate3d(0, -8px, 0); }
  to { opacity: 1; transform: translate3d(0, 0, 0); }
}
.industry-setup-wizard-stage[data-flow-direction="forward"] {
  animation: industry-setup-wizard-stage-forward .26s cubic-bezier(.22,1,.36,1) both;
}
.industry-setup-wizard-stage[data-flow-direction="backward"] {
  animation: industry-setup-wizard-stage-backward .24s cubic-bezier(.22,1,.36,1) both;
}
@media (prefers-reduced-motion: reduce) {
  .industry-setup-wizard-stage {
    animation: none !important;
    transform: none !important;
  }
  [data-industry-setup-wizard] * {
    scroll-behavior: auto !important;
    transition-duration: 0s !important;
  }
  [data-industry-setup-wizard] button:hover {
    transform: none !important;
  }
}
`;

const VIEWPORT_CLASSES: Record<IndustrySetupWizardViewport, string> = {
  embedded: "relative flex h-full min-h-0 w-full flex-col overflow-hidden",
  fullscreen: "app-frame-viewport z-[100] flex flex-col",
};

const CONTENT_WIDTH_CLASSES: Record<IndustrySetupWizardContentWidth, string> = {
  full: "max-w-none",
  narrow: "max-w-3xl",
  wide: "max-w-7xl",
};

const WizardHeadingContext = createContext<string | null>(null);

const FOCUSABLE_ELEMENT_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "summary",
  "[contenteditable='true']",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

function focusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_ELEMENT_SELECTOR),
  ).filter(
    (element) =>
      !element.hidden &&
      element.getClientRects().length > 0 &&
      element.getAttribute("aria-hidden") !== "true" &&
      !element.closest("[inert]"),
  );
}

function finiteInteger(value: number, fallback: number): number {
  return Number.isFinite(value) ? Math.trunc(value) : fallback;
}

export function normalizeIndustrySetupWizardProgress(
  stepIndex: number,
  stepCount: number,
): NormalizedProgress {
  const total = Math.max(1, finiteInteger(stepCount, 1));
  const boundedIndex = Math.min(
    total - 1,
    Math.max(0, finiteInteger(stepIndex, 0)),
  );
  const current = boundedIndex + 1;

  return {
    current,
    percent: (current / total) * 100,
    total,
  };
}

export function IndustrySetupQuestionStage({
  children,
  className = "",
  description,
  eyebrow,
  title,
}: IndustrySetupQuestionStageProps) {
  const contextualHeadingId = useContext(WizardHeadingContext);
  const fallbackHeadingId = useId();
  const headingId = contextualHeadingId ?? fallbackHeadingId;

  return (
    <div
      className={["space-y-7 sm:space-y-9", className].filter(Boolean).join(" ")}
      data-industry-setup-question="true"
    >
      <header className="max-w-2xl text-left">
        {eyebrow ? (
          <div className="mb-3 text-sm font-medium text-text-muted">{eyebrow}</div>
        ) : null}
        <h1
          className="text-balance text-[1.75rem] font-bold leading-tight tracking-tight text-text sm:text-[2.25rem]"
          id={headingId}
          tabIndex={-1}
        >
          {title}
        </h1>
        {description ? (
          <div className="mt-3 max-w-xl text-pretty text-[15px] leading-6 text-text-secondary sm:text-base">
            {description}
          </div>
        ) : null}
      </header>
      {children}
    </div>
  );
}

export function IndustrySetupWizardShell({
  auxiliaryAction,
  backAction,
  children,
  className = "",
  contentWidth = "narrow",
  direction = "forward",
  feedback,
  focusOnStageChange = true,
  headerEnd,
  headerStart,
  loading = null,
  primaryAction,
  progressAriaLabel,
  progressLabel,
  showProgress = true,
  stageKey,
  stepCount,
  stepIndex,
  topbar,
  viewport = "fullscreen",
}: IndustrySetupWizardShellProps) {
  const shellRef = useRef<HTMLDivElement | null>(null);
  const scrollPanelRef = useRef<HTMLDivElement | null>(null);
  const generatedHeadingId = useId();
  const headingId = `industry-setup-heading-${generatedHeadingId.replaceAll(":", "")}`;
  const progress = normalizeIndustrySetupWizardProgress(stepIndex, stepCount);
  const headingContext = useMemo(() => headingId, [headingId]);

  useEffect(() => {
    const shell = shellRef.current;
    if (!shell) return;

    const previouslyFocused =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (viewport === "embedded" && !shell.contains(event.target as Node)) return;

      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (viewport !== "fullscreen" || event.key !== "Tab") return;

      const focusable = focusableElements(shell);
      if (focusable.length === 0) {
        event.preventDefault();
        shell.focus({ preventScroll: true });
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const activeElement = document.activeElement;

      if (
        !shell.contains(activeElement) ||
        !focusable.includes(activeElement as HTMLElement)
      ) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }

      if (event.shiftKey && activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);

      if (viewport === "fullscreen" && previouslyFocused?.isConnected) {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  }, [viewport]);

  useEffect(() => {
    if (!focusOnStageChange || loading) return;

    scrollPanelRef.current?.scrollTo({ behavior: "auto", top: 0 });
    const frameId = window.requestAnimationFrame(() => {
      const heading = document.getElementById(headingId);

      if (heading && scrollPanelRef.current?.contains(heading)) {
        heading.focus({ preventScroll: true });
      }
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [focusOnStageChange, headingId, loading, stageKey]);

  return (
    <div
      aria-busy={loading ? "true" : undefined}
      className={[
        VIEWPORT_CLASSES[viewport],
        "bg-[var(--app-canvas-color)] text-text",
        className,
      ].filter(Boolean).join(" ")}
      data-industry-setup-viewport={viewport}
      data-industry-setup-wizard="true"
      ref={shellRef}
      tabIndex={viewport === "fullscreen" ? -1 : undefined}
    >
      <style>{INDUSTRY_SETUP_WIZARD_MOTION_STYLES}</style>

      {topbar ?? (
        <header className="relative z-20 flex min-h-16 shrink-0 items-center gap-3 px-4 py-3 sm:px-6 lg:min-h-24 lg:px-8">
          <span aria-hidden="true" className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-primary text-white">
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14m-6-6 6 6-6 6" />
            </svg>
          </span>
          <div className="min-w-0 flex-1 text-sm font-semibold sm:text-base">{headerStart}</div>
          {headerEnd ? <div className="shrink-0">{headerEnd}</div> : null}
        </header>
      )}

      {loading ? (
        <div className="flex min-h-0 flex-1 items-center justify-center overflow-y-auto overscroll-contain px-4 py-6 sm:px-8">
          <div className="my-auto w-full max-w-xl rounded-[32px] bg-surface p-2 sm:p-6" data-industry-setup-loading="true">
            {loading.eyebrow ? <div className="px-6 pt-5 text-center text-sm font-medium text-primary">{loading.eyebrow}</div> : null}
            <LoadingState label={loading.progressLabel} title={loading.title} description={loading.description} className="min-h-80 sm:min-h-96" />
            {loading.detail ? <div className="px-6 pb-6 text-center text-sm text-text-secondary">{loading.detail}</div> : null}
          </div>
        </div>
      ) : (
        <>
          {showProgress ? (
            <div className="mx-auto w-full max-w-3xl shrink-0 px-4 pb-4 sm:px-6 lg:pb-6">
              <div className="mb-3 text-xs font-semibold text-text-secondary">{progressLabel}</div>
              <div
                aria-label={progressAriaLabel ?? progressLabel}
                aria-valuemax={progress.total}
                aria-valuemin={1}
                aria-valuenow={progress.current}
                aria-valuetext={progressLabel}
                className="flex h-1.5 gap-1.5"
                role="progressbar"
              >
                {Array.from({ length: progress.total }, (_, index) => (
                  <span key={index} className={`h-full min-w-0 flex-1 rounded-full transition-colors duration-200 motion-reduce:transition-none ${index < progress.current ? "bg-primary" : "bg-primary/10"}`} />
                ))}
              </div>
            </div>
          ) : null}

          <div
            className="relative z-10 min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 pb-4 sm:px-6 lg:px-8 lg:pb-8"
            ref={scrollPanelRef}
          >
            <section
              aria-labelledby={headingId}
              className={[
                "industry-setup-wizard-stage mx-auto flex w-full flex-col rounded-[28px] bg-surface p-5 sm:rounded-[32px] sm:p-8 lg:p-10",
                CONTENT_WIDTH_CLASSES[contentWidth],
              ].join(" ")}
              data-flow-direction={direction}
              key={stageKey}
            >
              <WizardHeadingContext.Provider value={headingContext}>
                {children}
              </WizardHeadingContext.Provider>

              {feedback ? (
                <div className="mt-6 max-w-3xl" data-industry-setup-feedback="true">
                  {feedback}
                </div>
              ) : null}

            </section>
          </div>
          {backAction || auxiliaryAction || primaryAction ? (
            <footer className="shrink-0 bg-surface px-4 pt-3 sm:px-6 lg:bg-transparent lg:px-8" style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}>
              <div className="mx-auto flex w-full max-w-3xl items-center justify-between gap-3" data-industry-setup-actions="true">
                <div className="shrink-0">{backAction}</div>
                <div className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-2 [&>*]:max-w-full">
                  {auxiliaryAction}
                  {primaryAction}
                </div>
              </div>
            </footer>
          ) : null}
        </>
      )}
    </div>
  );
}
